import { config } from '@/config/env';
import { logger } from '@/logger';

import type {
	FireflyEditorLoader,
	FireflyImageEditorConfig,
	FireflyImageEditorHandle,
} from './types';

/**
 * Production loader for the Firefly image-editor bundle.
 *
 * This is the ONLY module in Bridgeway that knows about the bundle URL and the
 * `<firefly-image-editor>` custom element. It injects a `<script type="module">`
 * tag for the self-registering bundle served same-origin from `public/editor-assets/`,
 * mounts the element, wires Bridgeway's auth + no-paywall host, and adapts the
 * element's API to the Bridgeway-facing {@link FireflyImageEditorHandle}.
 *
 * We build the bundle ourselves from the firefly-platform source (used as a
 * local build tool): `vite.image-editor.config.ts` + `lib/firefly-image-editor-entry.ts`
 * emit a single `firefly-image-editor.js` exposing `createFireflyImageEditor`.
 * That artifact is committed under `public/editor-assets/` and shipped through
 * the normal `vite build` static pipeline; no dependency on the FFP team
 * publishing anything. Until the artifact is in place, this loader throws a
 * clear, catchable error so the modal shows an "editor unavailable" state
 * instead of a white screen. Tests inject a fake via `useFireflyEditor({ loader })`.
 */

/** URL of the editor bundle. Served same-origin from Bridgeway's public/ dir. */
function editorBundleUrl(): string {
	// The bundle (built from firefly-platform source via vite.image-editor.config.ts)
	// is copied to `public/editor-assets/firefly-image-editor.js`, so it is served
	// at the app origin. An env override allows pointing at a ui-proxy/CDN copy.
	const override = (config as { FIREFLY_EDITOR_BUNDLE_URL?: string })
		.FIREFLY_EDITOR_BUNDLE_URL;
	return override ?? '/editor-assets/firefly-image-editor.js';
}

/**
 * Resolve a cache-busting version for the bundle URL.
 *
 * The bundle is a large static file served from `public/` with no content hash
 * in its name, so a browser will happily reuse a STALE cached copy across
 * rebuilds (this caused a "Generate button does nothing" report after the
 * bundle was fixed — the browser kept serving the old broken build). A sibling
 * `firefly-image-editor.version.txt` is written next to the bundle at build/copy
 * time; we fetch it with `cache: 'no-store'` and append it as `?v=`, so a new
 * build is always picked up. If the version file is missing (older copies), fall
 * back to the bundle's `Last-Modified`/`ETag`, then to no param.
 */
async function resolveBundleVersion(bundleUrl: string): Promise<string> {
	// Don't version an explicit override URL — it is expected to be hashed/CDN.
	const override = (config as { FIREFLY_EDITOR_BUNDLE_URL?: string })
		.FIREFLY_EDITOR_BUNDLE_URL;
	if (override) return '';
	const base = bundleUrl.replace(/\.js$/, '');
	try {
		const res = await fetch(`${base}.version.txt`, { cache: 'no-store' });
		if (res.ok) {
			const v = (await res.text()).trim();
			if (v) return v;
		}
	} catch {
		// version file optional — fall through to HEAD-based stamp
	}
	try {
		const head = await fetch(bundleUrl, { method: 'HEAD', cache: 'no-store' });
		const stamp =
			head.headers.get('etag') ?? head.headers.get('last-modified') ?? '';
		if (stamp) return encodeURIComponent(stamp.replace(/[^\w.-]/g, ''));
	} catch {
		// ignore — last resort is no version param
	}
	return '';
}

/**
 * Shape the published bundle is expected to expose as its default export: a
 * factory mirroring {@link FireflyImageEditorLoader}. Defined here (not imported
 * from FFP) so Bridgeway has zero compile-time dependency on firefly-platform.
 */
interface FireflyBundleModule {
	createFireflyImageEditor: (
		config: FireflyImageEditorConfig,
	) => Promise<FireflyImageEditorHandle>;
}

function isBundleModule(mod: unknown): mod is FireflyBundleModule {
	return (
		typeof mod === 'object' &&
		mod !== null &&
		typeof (mod as FireflyBundleModule).createFireflyImageEditor === 'function'
	);
}

interface WindowWithEditor extends Window {
	__fireflyImageEditor__?: FireflyBundleModule;
}

/**
 * Load the bundle once by injecting a `<script type="module">` and resolving
 * when it assigns `window.__fireflyImageEditor__`. We use a script tag rather
 * than an ES `import()` because the bundle is served from the app's `public/`
 * dir, which Vite's dev server refuses to expose to the module graph
 * ("can only be referenced via HTML tags"). The script-tag path works in dev
 * and prod, and supports the bundle's top-level await. Cached after first load.
 */
let bundlePromise: Promise<FireflyBundleModule> | null = null;

function loadBundleModule(url: string): Promise<FireflyBundleModule> {
	const w = window as WindowWithEditor;
	if (w.__fireflyImageEditor__)
		return Promise.resolve(w.__fireflyImageEditor__);
	if (bundlePromise) return bundlePromise;

	bundlePromise = new Promise<FireflyBundleModule>((resolve, reject) => {
		const script = document.createElement('script');
		script.type = 'module';
		script.src = url;
		script.onload = () => {
			// The module's top-level await runs before the global is set, but the
			// `load` event fires after evaluation completes, so it's ready here.
			const mod = (window as WindowWithEditor).__fireflyImageEditor__;
			if (isBundleModule(mod)) resolve(mod);
			else
				reject(new Error('Firefly editor bundle did not expose the factory'));
		};
		script.onerror = () => {
			bundlePromise = null;
			reject(new Error(`Firefly editor bundle failed to load from ${url}`));
		};
		document.head.appendChild(script);
	});
	return bundlePromise;
}

export const loadFireflyEditor: FireflyEditorLoader = async (
	editorConfig,
): Promise<FireflyImageEditorHandle> => {
	const baseUrl = editorBundleUrl();
	const version = await resolveBundleVersion(baseUrl);
	const url = version
		? `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}v=${version}`
		: baseUrl;
	let mod: FireflyBundleModule;
	try {
		mod = await loadBundleModule(url);
	} catch (err) {
		logger.error('Failed to load Firefly editor bundle', err);
		throw err instanceof Error ? err : new Error(String(err));
	}
	return mod.createFireflyImageEditor(editorConfig);
};
